// Landing page for the tutor-side BMD Report feature. Lists the students the
// authenticated tutor has access to (via tbl_session ↔ tbl_student_enrolment).
// Clicking a student opens that student's BMD report at /bmd-report/:key.

import React from "react"
import { Container } from "reactstrap"
import { Link, useNavigate } from "react-router-dom"
import { getMyStudentsForReport } from "../../modules/bmd/services/bmd-report.service"
import type { BmdReportStudent } from "../../modules/bmd/types/bmd-report.types"

const BmdReportsListPage: React.FC = () => {
  const navigate = useNavigate()
  const [students, setStudents] = React.useState<BmdReportStudent[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [search, setSearch] = React.useState<string>("")

  React.useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    getMyStudentsForReport(controller.signal)
      .then((data) => {
        setStudents(data.students)
        setLoading(false)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        setError(err?.message || "Failed to load students")
        setLoading(false)
      })
    return () => controller.abort()
  }, [])

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return students
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.enrolment_year_level_name || "").toLowerCase().includes(q),
    )
  }, [students, search])

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0 fw-bold">Diagnostic Reports</h3>
        </div>

        <div className="bg-white rounded shadow-sm p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <input
              type="search"
              className="form-control"
              style={{ maxWidth: "320px" }}
              placeholder="Search by student name or year"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="text-muted font-size-13">
              {filtered.length} student{filtered.length === 1 ? "" : "s"}
            </span>
          </div>

          {loading ? (
            <p className="text-muted mb-0">Loading…</p>
          ) : error ? (
            <p className="text-danger mb-0">{error}</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted mb-0">No students found.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Year</th>
                    <th className="text-end" style={{ width: "180px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.pk_student_key}>
                      <td className="fw-medium">{s.name}</td>
                      <td className="text-muted">
                        {s.enrolment_year_level_name || "—"}
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() =>
                            navigate(`/bmd-report/${s.pk_student_key}`)
                          }
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}

export default BmdReportsListPage
